import { Button } from 'lego-on-react';
import React from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/UserRatings';
import block from 'propmods';

import { getSkillReviews } from '../../api';
import { moment } from '../../lib/moment';
import { UserReview } from '../../model/reviews';
import { Skill } from '../../model/skill';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { Loading } from '../Loading/Loading';
import { PageHeadline } from '../Typo/Typo';

import Star from './Star';

import './RatingPage.scss';

const t = i18n(keyset);
const b = block('RatingPage');

interface Props {
    skill: Skill;
}

interface State {
    items: UserReview[];
    hasMore: boolean;
    isLoading: boolean;
    page: number;
}

const rb = block('Reviews');

const Stars: React.SFC<{ rating: number }> = ({ rating }) => (
    <>
        {Array.from(Array(5).keys(), (x) => x + 1).map((x) => (
            <span key={x} {...rb('ratingStar')}>
                <Star active={rating > 0 && rating >= x} />
            </span>
        ))}
    </>
);

export function withLines(text: string | undefined) {
    if (!text) {
        return '';
    }

    return text
        .replace(/\n\s*\n/g, '\n')
        .split('\n')
        .map((x, i) => (
            <span key={i}>
                {x}
                <br />
            </span>
        ));
}

export default class RatingPage extends React.Component<Props, State> {
    public state: State = {
        isLoading: true,
        hasMore: true,
        items: [],
        page: 0,
    };

    public componentDidMount() {
        this.loadMore().catch(console.error);
    }

    private loadMore = async () => {
        const { skill } = this.props;
        const { page, items, hasMore } = this.state;
        if (!hasMore) {
            return;
        }

        try {
            this.setState({ isLoading: true });
            const newPage = page + 1;
            const result = await getSkillReviews(skill.id, skill.channel, newPage);
            this.setState({ items: [...items, ...result.items], page: newPage, hasMore: result.hasMore });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    public render() {
        const { items, isLoading, hasMore, page } = this.state;

        if (page === 0 && isLoading) {
            return (
                <div {...b()}>
                    <PageHeadline title={t('header')} />
                    <Loading centered />
                </div>
            );
        }

        return (
            <ErrorBoundary withHomepageLink>
                <div {...b()}>
                    <PageHeadline title={t('header')} />
                    {items && items.length ? (
                        <>
                            <table {...rb()} cellSpacing="0">
                                <thead>
                                    <tr>
                                        <th {...rb('ratingHeader')}>{t('table-rating')}</th>
                                        <th {...rb('dateHeader')}>{t('table-date')}</th>
                                        <th {...rb('reviewTextHeader')}>{t('table-feedback')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(({ rating, reviewText, createdAt }, i) => (
                                        <tr key={i} {...rb('review')}>
                                            <td {...rb('rating')}>
                                                <Stars rating={rating} />
                                            </td>
                                            <td {...rb('date')}>{moment(createdAt).format('DD.MM.YYYY HH:mm')}</td>
                                            <td {...rb('reviewText')}>{withLines(reviewText)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {hasMore ? (
                                <div {...rb('loadMore')}>
                                    <Button
                                        theme="action"
                                        size="m"
                                        onClick={() => this.loadMore()}
                                        disabled={isLoading}
                                    >
                                        {t('download-more')}
                                    </Button>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div>{t('no-feedback')}</div>
                    )}
                </div>
            </ErrorBoundary>
        );
    }
}
